import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getSeatableReservations,
  seatReservationFromOrdering,
  type OrderingSeatTarget,
} from "../../../../../api/reservations";

// Под ["reservations"] — всяко действие в модул „Резервации" го опреснява.
export const SEATABLE_KEY = ["reservations", "seatable"] as const;

// WS за резервациите няма: отвореният модал пита на 15 s (като екрана на Резервации).
const POLL_MS = 15_000;

/**
 * Предстоящите за „Настани" + отворените сметки — докато модалът е отворен
 * (на 15 s). Режимът „Схема" ги чете без интервал (`poll: false`): опреснява
 * ги при всеки клик по маса, като сметките — всяко четене синхронизира
 * настанените със сметките в BE (запис), а терминалът стои включен цял ден.
 */
export const useSeatableReservations = ({
  enabled,
  poll = true,
}: {
  enabled: boolean;
  poll?: boolean;
}) =>
  useQuery({
    queryKey: SEATABLE_KEY,
    queryFn: getSeatableReservations,
    enabled,
    staleTime: 0,
    refetchInterval: poll ? POLL_MS : false,
    refetchIntervalInBackground: false,
    // Без интервал — и без четене при фокус (всяко четене е запис в BE).
    ...(poll ? {} : { refetchOnWindowFocus: false }),
  });

/**
 * „Настани" от Маркиране. Сметките се изчакват (`await`) преди `onSuccess`
 * на извикващия — новата сметка трябва да е в списъка, преди да се избере,
 * иначе автоматичният избор скача на първата маса.
 */
export const useSeatFromOrdering = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, target, separateBills }: { id: number; target: OrderingSeatTarget; separateBills: boolean }) =>
      seatReservationFromOrdering(id, target, separateBills),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["reservations"] }),
        queryClient.invalidateQueries({ queryKey: ["bills"] }),
      ]),
  });
};
